<template>
  <div>
    <Loading v-if="loading" />
    <div v-else>
      <ShowPageHeader :backLabel="t('back_label')" backRoute="/merchant_profiles" :title="profile.fullName">
        <template #badges>
          <FieldSpan :value="profile.translatedRole" style-to-add="bg-blue-100 text-blue-800" />
        </template>
        <template #actions>
          <SecondaryButton @click="updateModalOpen = true">{{ t("edit_button") }}</SecondaryButton>
        </template>
      </ShowPageHeader>

      <FullScreenModal :open="updateModalOpen" @close="updateModalOpen = false">
        <MerchantProfileFormEdit :merchantProfileId="profile.id" @success="profileUpdated" />
      </FullScreenModal>

      <div class="mt-6">
        <div class="bg-white rounded-2xl shadow-md p-6 mx-auto">
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2">
            <StackedField :label="t('id_label')">
              <FieldSpan style-to-add="bg-purple-600 text-white" :value="profile.id" />
            </StackedField>
            <StackedField :label="t('last_name_label')" :value="profile.lastName" />
            <StackedField :label="t('email_label')" :value="profile.email" />
            <StackedField :label="t('first_name_label')" :value="profile.firstName" />
            <StackedField v-if="profile.region" :label="t('region_label')" :value="profile.region" />
            <StackedField :label="t('signed_up_label')">
              <FieldSpan
                :style-to-add="profile.signedUp ? 'text-green-800 bg-green-200' : 'text-red-800 bg-red-200'"
                :value="profile.signedUp ? t('signed_up_yes') : t('signed_up_no')"
              />
            </StackedField>
            <CopyableFieldRow
              v-if="!profile.signedUp && profile.signupUrl"
              :label="t('signup_link_label')"
              :value="profile.signupUrl"
            />
          </div>
        </div>
      </div>

      <div class="mt-6">
        <h2 class="text-xl font-bold pb-3">{{ t("stores_section_title") }}</h2>
        <Table :columns="storeTableColumns" :data="profileStores" :isRowClickable="true" @rowClick="goToStore">
          <template #status="{ row }">
            <FieldSpan
              translation-prefix="store.status"
              :style-to-add="getStatusStyle(row.status)"
              :value="row.status"
            />
          </template>
        </Table>
        <Pagination
          :currentPage="currentPage"
          :totalPages="totalPages"
          :totalCount="totalCount"
          @changePage="handleChangePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  Loading,
  ShowPageHeader,
  StackedField,
  Table,
  Pagination,
  SecondaryButton,
  FullScreenModal,
  CopyableFieldRow,
  FieldSpan,
} from "@estaly/ui";
import { useI18n } from "vue-i18n";
import { mapActions, mapGetters } from "vuex";
import MerchantProfileFormEdit from "@/components/merchant_profiles/MerchantProfileFormEdit.vue";

export default {
  setup() {
    const { t } = useI18n();
    return { t };
  },
  components: {
    Loading,
    ShowPageHeader,
    StackedField,
    Table,
    Pagination,
    SecondaryButton,
    FullScreenModal,
    CopyableFieldRow,
    FieldSpan,
    MerchantProfileFormEdit,
  },
  data() {
    return {
      updateModalOpen: false,
      profile: {},
      currentPage: 1,
      totalPages: 0,
      totalCount: 0,
      profileStores: [],
    };
  },
  computed: {
    ...mapGetters("stores", ["getStatusStyle"]),

    storeTableColumns() {
      return [
        { label: this.t("store_id_column"), field: "id", widthClass: "w-16" },
        { label: this.t("store_name_column"), field: "salesChannel" },
        { label: this.t("store_status_column"), field: "status", slot: "status" },
      ];
    },
    profileId() {
      return this.$route.params.id;
    },
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    ...mapActions("merchantProfiles", ["fetch"]),
    ...mapActions("stores", { fetchStores: "fetchIndex" }),
    ...mapActions("backoffice", ["withLoader"]),

    goToStore(store) {
      this.$router.push(`/stores/${store.id}`);
    },
    async fetchData() {
      await this.withLoader(async () => {
        this.profile = await this.fetch({ id: this.profileId });
        const result = await this.fetchStores({
          merchantProfileId: this.profileId,
          page: this.currentPage,
          perPage: 2000,
        });
        if (result) {
          this.profileStores = result.records;
          this.totalPages = result.page.totalPages;
          this.totalCount = result.page.totalCount;
        }
      });
    },
    async profileUpdated() {
      this.updateModalOpen = false;
      await this.fetchData();
    },
    async handleChangePage(page) {
      this.currentPage = page;
      await this.fetchData();
    },
  },
};
</script>

<i18n src="./MerchantProfileDetails.i18n.json"></i18n>
